import { useCallback, useEffect, useRef, useState } from 'react';

import { useSelector } from 'react-redux';

import Button from 'components/Button';
import Input from 'components/Input';
import { ENUM_TYPE_REPORT_STATUS } from 'constants/enum';
import useVideoCategory from 'hooks/useVideoCategory';

const RecentEvaluationAnalysisResults = ({ form, isReportRegistration = false, currentStatus, isRecently }) => {
  const { me } = useSelector((state) => state.auth);
  const { childrenDetail } = useSelector((state) => state.children);

  const { getAllParentVideoCategory } = useVideoCategory();

  const [parentVideoCategories, setParentVideoCategories] = useState([]);

  const isCallingApi = useRef(false);

  const initScores = useCallback(() => {
    if (form.values.scores.length === 0) {
      form.setFieldValue(
        'scores',
        parentVideoCategories.map((item) => ({
          parent_videocategory_id: Number(item.id),
          score: 0,
        }))
      );
    }
  }, [parentVideoCategories]);

  useEffect(() => {
    initScores();
  }, [initScores]);

  const _getAllParentVideoCategory = useCallback(async () => {
    if (parentVideoCategories.length === 0 && !isCallingApi.current) {
      isCallingApi.current = true;
      const response = await getAllParentVideoCategory();
      if (response) {
        setParentVideoCategories(response);
      }
    }
  }, [me]);

  useEffect(() => {
    _getAllParentVideoCategory();
  }, [_getAllParentVideoCategory]);

  const handleCheckDisabled = (status) => {
    return status === ENUM_TYPE_REPORT_STATUS.LOCK || status === ENUM_TYPE_REPORT_STATUS.FINISH;
  };

  const handleCheckChecked = (id, score) => {
    return form.values.scores.find((itemScore) => itemScore.parent_videocategory_id === id)?.score === score;
  };

  const handleSubmit = async (status) => {
    await form.setFieldValue('status', status);
    form.handleSubmit();
  };

  const handleChangeScore = (id, score) => {
    form.setFieldValue(
      'scores',
      form.values.scores.map((item) => (item.parent_videocategory_id === id ? { ...item, score } : item))
    );
  };

  return (
    <div className="h-full py-4 px-2 bg-white w-full">
      <p className="font-bold text-xl mb-2 px-2">{isRecently ? '直近の評価分析結果' : isReportRegistration ? '評価分析結果' : '評価分析結果'}</p>
      <div className="h-[calc(100%-52px)] overflow-auto py-2 px-2">
        <div className="px-4">
          <p className="mb-2">{'理学療法士(PT)からのコメント'}</p>
          <Input
            disabled={form.values.status === ENUM_TYPE_REPORT_STATUS.LOCK || form.values.status === ENUM_TYPE_REPORT_STATUS.FINISH}
            type="textarea"
            value={form.values.comment}
            name="comment"
            id="comment"
            onChange={(e) => form.setFieldValue('comment', e.target.value)}
            className="!w-full"
            classNameInput="!h-[216px] !rounded-lg !p-3 !resize-none !outline-none"
            invalid={form.errors.comment}
          />
        </div>

        <div className="pl-4 pr-[7px] mt-6 flex flex-col space-y-4">
          {parentVideoCategories.map((item, index) => (
            <div className="flex flex-col space-y-2" key={`parent-video-category-${index}`}>
              <p>{item.name}</p>
              <div className="flex gap-4">
                <Input
                  disabled={handleCheckDisabled(form.values.status)}
                  checked={handleCheckChecked(item.id, 1)}
                  onClick={(e) => handleChangeScore(Number(item.id), Number(e.target.value))}
                  value={1}
                  name={`scores.${index}.score`}
                  type="radio-cus"
                  text="1.これからの力"
                  className={`!w-auto whitespace-nowrap ${isReportRegistration || (handleCheckChecked(item.id, 1) && handleCheckDisabled(form.values.status)) ? '' : 'grayscale opacity-25'}`}
                />
                <Input
                  disabled={handleCheckDisabled(form.values.status)}
                  checked={handleCheckChecked(item.id, 2)}
                  onClick={(e) => handleChangeScore(Number(item.id), Number(e.target.value))}
                  value={2}
                  name={`scores.${index}.score`}
                  type="radio-cus"
                  text="2.頑張っている"
                  className={`!w-auto whitespace-nowrap ${isReportRegistration || (handleCheckChecked(item.id, 2) && handleCheckDisabled(form.values.status)) ? '' : 'grayscale opacity-25'}`}
                />
                <Input
                  disabled={handleCheckDisabled(form.values.status)}
                  checked={handleCheckChecked(item.id, 3)}
                  onClick={(e) => handleChangeScore(Number(item.id), Number(e.target.value))}
                  value={3}
                  name={`scores.${index}.score`}
                  type="radio-cus"
                  text="3.上手にできている"
                  className={`!w-auto whitespace-nowrap ${isReportRegistration || (handleCheckChecked(item.id, 3) && handleCheckDisabled(form.values.status)) ? '' : 'grayscale opacity-25'}`}
                />
              </div>
            </div>
          ))}
        </div>

        {isReportRegistration && (
          <div className="mt-4 py-2 flex justify-end items-center gap-4">
            <Button
              onClick={() => {
                if (currentStatus === ENUM_TYPE_REPORT_STATUS.PROCESSING) {
                  handleSubmit(ENUM_TYPE_REPORT_STATUS.PROCESSING);
                }
              }}
              type="button"
              color="outline"
              text="一時保存"
              className={`!rounded-full ${currentStatus !== ENUM_TYPE_REPORT_STATUS.PROCESSING ? '!bg-gray-651 !text-white cursor-not-allowed' : ''}`}
            />
            <Button
              onClick={() => {
                if (currentStatus !== ENUM_TYPE_REPORT_STATUS.FINISH) {
                  handleSubmit(ENUM_TYPE_REPORT_STATUS.LOCK);
                }
              }}
              type="button"
              color={`${currentStatus !== ENUM_TYPE_REPORT_STATUS.FINISH ? 'primary-outline' : 'outline'}`}
              text="評価結果の入力完了"
              className={`!rounded-full ${currentStatus === ENUM_TYPE_REPORT_STATUS.FINISH ? '!bg-gray-651 !text-white cursor-not-allowed' : ''}`}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default RecentEvaluationAnalysisResults;
